import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import type { EstadoAsamblea } from '@condo/contracts';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useAsamblea } from './api';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatLongDate, formatShortDate } from '@/lib/format/date';

const ESTADO_LABELS: Record<EstadoAsamblea, string> = {
  borrador: 'Borrador',
  convocada: 'Convocada',
  en_votacion: 'En votacion',
  sin_quorum: 'Sin quorum',
  cerrada: 'Cerrada',
  cerrada_sin_efecto: 'Cerrada sin efecto',
  anulada: 'Anulada',
};

const VARIANT_MAP: Record<
  EstadoAsamblea,
  'neutral' | 'primary' | 'warning' | 'success' | 'danger'
> = {
  borrador: 'neutral',
  convocada: 'primary',
  en_votacion: 'warning',
  sin_quorum: 'danger',
  cerrada: 'success',
  cerrada_sin_efecto: 'neutral',
  anulada: 'neutral',
};

/**
 * Vista de comite: solo lectura del detalle de la asamblea.
 * Acciones de gestion quedan en el panel de administrador.
 */
export function ComiteAsambleaPanel({ asambleaId }: { asambleaId: string }) {
  const { condoId } = useCondoContext();
  const { data: asamblea, isLoading } = useAsamblea(condoId, asambleaId);

  if (isLoading || !asamblea) {
    return (
      <PageContainer width="medium">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64" />
      </PageContainer>
    );
  }

  const { estado, materias } = asamblea;
  const isClosed = estado === 'cerrada' || estado === 'cerrada_sin_efecto';

  return (
    <PageContainer width="medium">
      <Button variant="ghost" asChild size="sm" className="mb-4">
        <Link to={`/app/condominios/${condoId}/asambleas`}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>
      </Button>

      <PageHeader
        eyebrow={`Asamblea ${asamblea.tipo} · Citacion ${asamblea.numeroCitacion}`}
        title={formatLongDate(asamblea.fechaProgramada)}
        subtitle={asamblea.lugar}
        meta={
          <div className="mt-2">
            <Badge variant={VARIANT_MAP[estado]}>{ESTADO_LABELS[estado]}</Badge>
          </div>
        }
        action={
          isClosed ? (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/app/condominios/${condoId}/asambleas/${asambleaId}/acta`}>
                <FileText className="h-4 w-4" />
                Ver acta
              </Link>
            </Button>
          ) : undefined
        }
        ruleSpacing="mb-8"
      />

      {/* Plazos */}
      <div className="mb-8 grid grid-cols-2 gap-3">
        <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-4">
          <p className="eyebrow text-[0.6rem]">Votacion desde</p>
          <p className="font-display-tight mt-1 text-lg font-semibold">
            {formatShortDate(asamblea.plazoVotacionDesde)}
          </p>
        </div>
        <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-4">
          <p className="eyebrow text-[0.6rem]">Votacion hasta</p>
          <p className="font-display-tight mt-1 text-lg font-semibold">
            {formatShortDate(asamblea.plazoVotacionHasta)}
          </p>
        </div>
      </div>

      {/* Materias (read-only) */}
      <h2 className="font-display text-xl font-semibold">Orden del dia</h2>
      {materias.length === 0 ? (
        <p className="mt-4 text-sm text-[var(--color-ink-muted)]">Sin materias registradas.</p>
      ) : (
        <ol className="mt-4 space-y-2">
          {materias.map((m) => (
            <li
              key={m.id}
              className="flex items-start gap-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-4"
            >
              <span className="font-display text-sm font-semibold text-[var(--color-ink-muted)]">
                {String(m.orden + 1).padStart(2, '0')}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-display text-sm font-semibold truncate">{m.titulo}</p>
                {m.descripcion && (
                  <p className="mt-0.5 text-xs text-[var(--color-ink-muted)] truncate">
                    {m.descripcion}
                  </p>
                )}
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <Badge variant="neutral" className="text-[0.625rem]">
                    {m.categoria.replace(/_/g, ' ')}
                  </Badge>
                  {m.mayoriaRequerida && (
                    <Badge variant="primary" className="text-[0.625rem]">
                      {m.mayoriaRequerida.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </PageContainer>
  );
}
